import { CommonModule } from '@angular/common'
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    DestroyRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    inject,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms'
import {
    EI_TABLE_DEFAULTS,
    EI_TABLE_TEXT,
    EI_TABLE_DIMENSIONS,
} from './constants/ei-table.constants'
import { REGEX_PATTERNS } from '@app/core/constants/regex.constants'
import { IDropdownConfig } from '@app/shared/models/dropdown.model'
import { EiDropdownComponent } from '../ei-dropdown/ei-dropdown.component'
import { EiSearchBarComponent } from '../ei-search-bar/ei-search-bar.component'
import { EiButtonComponent } from '../ei-button/ei-button.component'
import { formatCellText, getCellValue } from './helpers/ei-table.data.helpers'
import {
    applyFiltersAndSort,
    getRowId as getRowIdHelper,
    handlePageSizeChange,
    handleVisibleColumnsChange,
} from './helpers/ei-table.component.helpers'
import {
    buildVisiblePages,
    safeGetLocalStorage,
    safeSetLocalStorage,
    toggleSort,
} from './helpers/ei-table.state.helpers'
import {
    buildFilterDropdownConfigs,
    buildManageColumnsConfig,
    getSortIcon,
    getVisibleColumns,
    isActionsColumn,
    isDownloadColumn,
    syncSelectedVisibleColumnsModel,
} from './helpers/ei-table.column.helpers'
import { getPagedRows } from './helpers/ei-table.pagination.helpers'
import {
    toggleAllRowsSelection as toggleAllRowsSelectionHelper,
    toggleRowInSelection as toggleRowInSelectionHelper,
} from './helpers/ei-table.selection.helpers'
import { createUpdatedRow, EditingCell } from './helpers/ei-table.edit.helpers'
import { buildPageSizeConfig } from './helpers/ei-table.config.helpers'
import {
    EiTableAction,
    EiTableCellEditCommit,
    EiTableColumn,
    EiTableRow,
    EiTableSortState,
} from './models/ei-table.model'
import { EDropdownTriggerMode } from '@app/shared/enums/ei-dropdown.enum'
import { SvgIconComponent } from '@app/shared/components/svg-icon/svg-icon.component'

type NgClassInput = string | string[] | Set<string> | Record<string, boolean> | null | undefined

@Component({
    selector: 'ei-table',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        EiDropdownComponent,
        EiSearchBarComponent,
        EiButtonComponent,
        SvgIconComponent,
    ],
    templateUrl: './ei-table.component.html',
    styleUrl: './ei-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EiTableComponent implements OnInit, OnChanges, OnDestroy {
    private changeDetectorRef = inject(ChangeDetectorRef)
    private readonly destroyRef = inject(DestroyRef)

    readonly Math = Math
    readonly text = EI_TABLE_TEXT
    readonly CHECKBOX_COLUMN_WIDTH = EI_TABLE_DIMENSIONS.checkboxColumnWidth
    readonly ROW_NUMBER_COLUMN_WIDTH = EI_TABLE_DIMENSIONS.rowNumberColumnWidth
    readonly DropdownTriggerMode = EDropdownTriggerMode

    @Input() serverSidePagination = false
    @Input() totalItemsExternal?: number
    @Input() pageIndexExternal?: number
    @Input() pageSizeExternal?: number

    /**
     * Optional "render mode" toggles so feature screens can embed EiTable
     * without the toolbar/footer chrome (e.g. custom filter rows).
     */
    @Input() hideToolbar = false
    @Input() hideFooter = false
    // When true, the tbody shows skeleton placeholder rows and the footer is non-interactive
    @Input() loading = false
    /** How many skeleton rows to paint while {@link loading} is true (clamped 1–50). */
    @Input() skeletonRowCount = 8
    /** Inline cell edit action rendering for Save/Cancel controls. */
    @Input() inlineEditActions: 'icons' | 'text' | 'none' = 'icons'
    /**
     * When true, the whole `<td>` is a click target to start inline edit (not only the text span).
     * Use on dense tables where cell padding is easier to hit than the label.
     */
    @Input() editableCellFullTdClick = false

    /**
     * Optional hook for row-level styling (e.g. TOTAL row highlight).
     */
    @Input() rowClassGetter?: (row: EiTableRow) => NgClassInput

    @Input() viewId: string = EI_TABLE_DEFAULTS.viewId
    @Input() rowIdKey: string = EI_TABLE_DEFAULTS.rowIdKey
    @Input() rows: EiTableRow[] = []
    /** When `rows` is empty, show this instead of the default "No data" label. */
    @Input() emptyTableMessage: string | null = null
    @Input() columns: EiTableColumn[] = []
    @Input() pageSizeOptions: readonly number[] = [...EI_TABLE_DEFAULTS.pageSizeOptions]
    @Input() actions: EiTableAction[] = []

    @Input() globalSearchPlaceholder: string | null = null
    @Input() recordsFoundSuffix: string | null = null
    @Input() externalSelectFilters: Record<
        string,
        string | number | (string | number)[] | null
    > | null = null
    @Input() manageColumnsLabel: string | null = null
    @Input() manageColumnsPlaceholder: string | null = null
    @Input() footerEntriesLabel: string | null = null
    @Input() useRangePaginationFormat = false
    @Input() serverPagination = false
    @Input() totalRecords?: number
    /** When set, the row with this id gets the --selected class and row clicks emit rowClick. */
    @Input() selectedRowId: string | number | null = null

    @Output() readonly cellEditCommit = new EventEmitter<EiTableCellEditCommit>()
    @Output() readonly actionClick = new EventEmitter<{ actionId: string; row: EiTableRow }>()
    @Output() readonly downloadClick = new EventEmitter<EiTableRow>()
    @Output() readonly rowSelectionChange = new EventEmitter<Set<string | number>>()
    @Output() readonly rowClick = new EventEmitter<EiTableRow>()
    @Output() readonly paginationChange = new EventEmitter<{
        pageIndex: number
        pageSize: number
    }>()

    @ContentChild('rowActionsPanel')
    rowActionsPanelTemplate?: TemplateRef<{ row: EiTableRow; close: () => void }>

    @ContentChild('toolbarSearch')
    toolbarSearchTemplate?: TemplateRef<{ value: string; valueChange: (v: string) => void }>

    @ContentChild('rightSideActionBtns')
    rightSideActionBtns?: TemplateRef<{ value: string; valueChange: (v: string) => void }>
    @Output() readonly pageChange = new EventEmitter<number>()
    @Output() readonly pageSizeChange = new EventEmitter<number>()
    @Output() readonly globalSearchChange = new EventEmitter<string>()
    @Output() readonly sortChange = new EventEmitter<EiTableSortState>()
    @Output() readonly selectFiltersChange = new EventEmitter<
        Record<string, string | number | (string | number)[] | null>
    >()

    // Search and filter state
    globalSearch = ''
    columnSearch: Record<string, string> = {}
    selectFilters: Record<string, string | number | (string | number)[] | null> = {}

    // Sort and pagination state
    sort: EiTableSortState = { activeKey: '', direction: '' }
    private pageIndex = 0
    pageSize: number = EI_TABLE_DEFAULTS.pageSize

    // Column visibility state
    visibleColumnKeys: string[] = []
    selectedVisibleColumnsModel: (string | number)[] = []
    manageColumnsConfig: IDropdownConfig = {
        label: this.text.manageColumnsLabel,
        placeholder: this.text.manageColumnsPlaceholder,
        options: [],
    }
    rowActionsDropdownConfig: IDropdownConfig = { options: [] }
    filterDropdownConfigs: Record<string, IDropdownConfig> = {}

    // Row selection state
    selectedRowIds = new Set<string | number>()

    // Cell editing state
    editingCell: EditingCell = null
    editControl = new FormControl<string>('', { nonNullable: true })
    private editOriginalValue: unknown = null
    /** Inline message from {@link EiTableColumn.editValidator}; blocks commit while set. */
    editInlineError: string | null = null

    // Screen size state
    isMobileOrTablet = false
    private resizeListener?: () => void

    ngOnInit(): void {
        this.initializeScreenSize()
        this.loadPersistedState()
        this.initializeColumnConfigs()
        this.editControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.refreshEditInlineValidation()
            this.changeDetectorRef.markForCheck()
        })
    }

    ngOnDestroy(): void {
        if (typeof window !== 'undefined' && this.resizeListener) {
            window.removeEventListener('resize', this.resizeListener)
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['viewId']) {
            this.loadPersistedState()
            this.clearSearchAndFilterState()
            this.clearSelection()
        }
        if (
            changes['viewId'] ||
            changes['columns'] ||
            changes['manageColumnsLabel'] ||
            changes['manageColumnsPlaceholder']
        ) {
            this.initializeColumnConfigs()
        }
        if (changes['columns']) {
            // Re-read visibleColumnKeys from localStorage when columns change
            // This ensures we pick up the latest column visibility settings
            const getKey = (suffix: string) => `ei-table:${this.viewId}:${suffix}`
            this.visibleColumnKeys = safeGetLocalStorage<string[]>(getKey('visibleCols'), [])
            this.initializeColumnConfigs()
        }

        if (this.serverSidePagination) {
            if (changes['pageIndexExternal'] && this.pageIndexExternal !== undefined) {
                this.pageIndex = this.pageIndexExternal
            }
            if (changes['pageSizeExternal'] && this.pageSizeExternal !== undefined) {
                this.pageSize = this.pageSizeExternal
            }
        }
    }

    private clearSearchAndFilterState(): void {
        this.globalSearch = ''
        this.columnSearch = {}
        this.selectFilters = {}
        if (this.serverSidePagination && this.pageIndexExternal !== undefined) {
            this.pageIndex = this.pageIndexExternal
        } else {
            this.resetPageIndex()
        }
    }

    clearSelection(): void {
        this.selectedRowIds = new Set()
        this.rowSelectionChange.emit(new Set())
        this.changeDetectorRef.markForCheck()
    }

    private initializeScreenSize(): void {
        this.checkScreenSize()
        if (typeof window !== 'undefined') {
            this.resizeListener = () => this.checkScreenSize()
            window.addEventListener('resize', this.resizeListener)
        }
    }

    private checkScreenSize(): void {
        if (typeof window !== 'undefined') {
            const wasMobileOrTablet = this.isMobileOrTablet
            this.isMobileOrTablet = window.innerWidth < EI_TABLE_DIMENSIONS.mobileBreakpoint
            if (wasMobileOrTablet !== this.isMobileOrTablet) {
                this.changeDetectorRef.markForCheck()
            }
        }
    }

    private loadPersistedState(): void {
        const getKey = (suffix: string) => `ei-table:${this.viewId}:${suffix}`
        const skipPersistedPageSize =
            this.serverSidePagination && this.pageSizeExternal !== undefined
        if (!skipPersistedPageSize) {
            this.pageSize = safeGetLocalStorage<number>(getKey('pageSize'), this.pageSize)
        }
        this.visibleColumnKeys = safeGetLocalStorage<string[]>(getKey('visibleCols'), [])
    }

    private initializeColumnConfigs(): void {
        this.manageColumnsConfig = buildManageColumnsConfig(
            this.allColumns,
            this.manageColumnsLabel ?? this.text.manageColumnsLabel,
            this.manageColumnsPlaceholder ?? this.text.manageColumnsPlaceholder,
        )
        this.filterDropdownConfigs = buildFilterDropdownConfigs(this.allColumns)
        const syncedKeys = syncSelectedVisibleColumnsModel(this.allColumns, this.visibleColumnKeys)
        this.selectedVisibleColumnsModel = syncedKeys
        this.visibleColumnKeys = syncedKeys
    }

    get allColumns(): EiTableColumn[] {
        return this.columns ?? []
    }

    get visibleColumns(): EiTableColumn[] {
        return getVisibleColumns(this.allColumns, this.visibleColumnKeys)
    }

    /**
     * Get the number of columns without explicit widths for CSS calculation.
     */
    get columnsWithoutExplicitWidth(): number {
        return this.visibleColumns.filter((col) => !col.widthPx).length
    }

    get hasColumnSearchRow(): boolean {
        return this.visibleColumns.some((column) => !!column.columnSearch)
    }

    get effectiveSelectFilters(): Record<string, string | number | (string | number)[] | null> {
        return {
            ...this.selectFilters,
            ...(this.externalSelectFilters ?? {}),
        }
    }

    /** Global search is emitted to parents for server-side filtering; skip local filtering when enabled. */
    private get clientSideGlobalSearch(): string {
        return this.serverPagination || this.serverSidePagination ? '' : this.globalSearch
    }

    get filteredSortedRows(): EiTableRow[] {
        return applyFiltersAndSort(
            this.rows,
            this.visibleColumns,
            this.clientSideGlobalSearch,
            this.columnSearch,
            this.effectiveSelectFilters,
            this.sort,
        )
    }

    get totalItems(): number {
        if (this.serverPagination && this.totalRecords !== undefined) {
            return this.totalRecords
        }
        if (this.serverSidePagination && this.totalItemsExternal !== undefined) {
            return this.totalItemsExternal
        }
        return this.filteredSortedRows.length
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.totalItems / this.pageSize))
    }

    get currentPage(): number {
        return this.pageIndex + 1
    }

    get pagedRows(): EiTableRow[] {
        if (this.serverPagination) {
            return this.filteredSortedRows
        }
        if (this.serverSidePagination) {
            return this.filteredSortedRows
        }
        const { rows, normalizedPageIndex } = getPagedRows(
            this.filteredSortedRows,
            this.pageIndex,
            this.pageSize,
            this.totalPages,
        )
        if (this.pageIndex !== normalizedPageIndex) this.pageIndex = normalizedPageIndex
        return rows
    }

    /**
     * Normalize page index to ensure it's within valid bounds.
     * Should be called when filters change or total pages might have changed.
     */
    private normalizePageIndex(): void {
        const maxPageIndex = Math.max(0, this.totalPages - 1)
        if (this.pageIndex > maxPageIndex) {
            this.pageIndex = maxPageIndex
        }
    }

    get visiblePages(): number[] {
        return this.isMobileOrTablet
            ? [this.currentPage]
            : buildVisiblePages(this.currentPage, this.totalPages)
    }

    get pageSizeConfig(): IDropdownConfig {
        return buildPageSizeConfig(this.pageSizeOptions, this.text.pageSizeLabel)
    }

    get skeletonRowIndices(): number[] {
        const skeletonRowTotal = Math.max(1, Math.min(this.skeletonRowCount, 50))
        return Array.from({ length: skeletonRowTotal }, (_, rowIndex) => rowIndex)
    }

    get showCheckboxColumn(): boolean {
        return this.columns.some((column) => column.showCheckbox === true)
    }

    get hasSelection(): boolean {
        return this.showCheckboxColumn && this.selectedRowIds.size > 0
    }

    get showRowNumberColumn(): boolean {
        return this.columns.some((column) => column.showRowNumber === true)
    }

    get currentPageIndex(): number {
        return this.pageIndex
    }

    get isFirstPage(): boolean {
        return this.pageIndex <= 0
    }

    get isLastPage(): boolean {
        return this.pageIndex >= this.totalPages - 1
    }

    trackRow = (_: number, row: EiTableRow): string | number => this.getRowId(row)
    getRowId = (row: EiTableRow): string | number => getRowIdHelper(row, this.rowIdKey)

    onVisibleColumnsChange(values: (string | number)[] | string | number | null): void {
        const { finalKeys, shouldResetPage } = handleVisibleColumnsChange(
            values,
            this.allColumns,
            this.viewId,
        )
        this.visibleColumnKeys = finalKeys
        this.selectedVisibleColumnsModel = finalKeys
        if (shouldResetPage) this.resetPageIndex()
    }

    getFilterDropdownConfig = (column: EiTableColumn): IDropdownConfig | null =>
        this.filterDropdownConfigs[column.key] ?? null

    onGlobalSearchChange = (searchValue: string | Event): void => {
        const value =
            typeof searchValue === 'string'
                ? searchValue
                : ((searchValue?.target as HTMLInputElement)?.value ?? '')

        this.globalSearch = value
        if (this.serverSidePagination || this.serverPagination) {
            this.globalSearchChange.emit(this.globalSearch)
        } else {
            this.globalSearchChange.emit(this.globalSearch)
            // Client-side: reset page and normalize
            this.resetPageIndex()
            this.normalizePageIndex()
        }
    }

    onColumnSearchChange = (columnKey: string, searchValue: string): void => {
        this.columnSearch = { ...this.columnSearch, [columnKey]: searchValue ?? '' }
        this.resetPageIndex()
        if (!this.serverSidePagination) {
            this.normalizePageIndex()
        }
    }

    onFilterChange = (
        columnKey: string,
        filterValue: string | number | (string | number)[] | null,
    ): void => {
        const normalizedValue = Array.isArray(filterValue)
            ? filterValue.length
                ? filterValue
                : null
            : (filterValue ?? null)
        this.selectFilters = { ...this.selectFilters, [columnKey]: normalizedValue }
        this.selectFiltersChange.emit(this.selectFilters)
        this.resetPageIndex()
        if (!this.serverSidePagination) {
            this.normalizePageIndex()
        }
    }

    onHeaderSort = (column: EiTableColumn): void => {
        if (column.sortable) {
            this.sort = toggleSort(column.key, this.sort)
            this.resetPageIndex()
            // Emit sort change event for server-side pagination
            if (this.serverSidePagination) {
                this.sortChange.emit(this.sort)
            }
        }
    }

    private emitPaginationChange(): void {
        this.paginationChange.emit({ pageIndex: this.pageIndex, pageSize: this.pageSize })
    }

    onPageSizeChange(value: string | number | (string | number)[] | null): void {
        const { newPageSize, shouldResetPage } = handlePageSizeChange(
            value,
            this.pageSize,
            this.viewId,
        )
        const isUnselected =
            value === null || value === '' || (Array.isArray(value) && value.length === 0)

        if (newPageSize !== null) {
            this.pageSize = newPageSize
            if (shouldResetPage) {
                if (this.serverSidePagination) {
                    this.pageIndex = 0
                } else {
                    this.resetPageIndex()
                }
            }
            this.emitPaginationChange()
            if (this.serverSidePagination) {
                this.pageSizeChange.emit(newPageSize)
            } else if (shouldResetPage) {
                this.resetPageIndex()
            }
        } else if (isUnselected) {
            const defaultPageSize = EI_TABLE_DEFAULTS.pageSize
            this.pageSize = defaultPageSize
            safeSetLocalStorage(`ei-table:${this.viewId}:pageSize`, defaultPageSize)
            if (this.serverSidePagination) {
                this.pageIndex = 0
            } else {
                this.resetPageIndex()
            }
            this.emitPaginationChange()
            if (this.serverSidePagination) {
                this.pageSizeChange.emit(this.pageSize)
            }
            // Force UI update so dropdown receives 10 and shows it (not placeholder)
            this.changeDetectorRef.detectChanges()
        }
    }

    onPrev = (): void => {
        if (this.pageIndex > 0) {
            this.pageIndex -= 1
            this.emitPaginationChange()
            if (this.serverSidePagination) {
                this.pageChange.emit(this.pageIndex)
            }
        }
    }

    onNext = (): void => {
        const maxPageIndex = Math.max(0, this.totalPages - 1)
        if (this.pageIndex < maxPageIndex) {
            this.pageIndex += 1
            this.emitPaginationChange()
            if (this.serverSidePagination) {
                this.pageChange.emit(this.pageIndex)
            }
        }
    }

    onGoTo = (pageNumber: number): void => {
        if (pageNumber < 1 || pageNumber > this.totalPages) return
        const maxPageIndex = Math.max(0, this.totalPages - 1)
        const targetPageIndex = pageNumber - 1
        this.pageIndex = Math.max(0, Math.min(maxPageIndex, targetPageIndex))
        this.emitPaginationChange()

        if (this.serverSidePagination) {
            this.pageChange.emit(this.pageIndex)
        }
    }

    isEllipsis = (pageNumber: number): boolean => {
        return pageNumber === -1 || pageNumber === -2
    }

    getClassSafeText = (text: string): string => {
        return text.toLowerCase().replace(REGEX_PATTERNS.WHITESPACE_GLOBAL, '-')
    }

    getRowNumber = (rowIndex: number): number => {
        return this.pageIndex * this.pageSize + rowIndex + 1
    }

    getStartRowNumber = (): number => {
        return this.totalItems ? this.pageIndex * this.pageSize + 1 : 0
    }

    getExpiryDateText = (value: string): string => {
        const normalized = (value ?? '').trim()
        const warningSeparatorIndex = normalized.indexOf(' (')
        return warningSeparatorIndex >= 0 ? normalized.slice(0, warningSeparatorIndex) : normalized
    }

    getExpiryWarningText = (value: string): string => {
        const normalized = (value ?? '').trim()
        const warningSeparatorIndex = normalized.indexOf(' (')
        if (warningSeparatorIndex < 0) return ''

        const warning = normalized.slice(warningSeparatorIndex + 2)
        return warning.endsWith(')') ? warning.slice(0, -1) : warning
    }

    getEndRowNumber = (): number => {
        return Math.min((this.pageIndex + 1) * this.pageSize, this.totalItems)
    }
    resetPageIndex = (): void => {
        this.pageIndex = 0
        if (this.serverSidePagination) {
            this.pageChange.emit(this.pageIndex)
        }
    }

    private getCurrentPageRowIds = (): (string | number)[] =>
        this.pagedRows.map((row) => this.getRowId(row))

    toggleRowSelection = (row: EiTableRow): void => {
        const rowId = this.getRowId(row)
        this.selectedRowIds = toggleRowInSelectionHelper(rowId, this.selectedRowIds)
        this.rowSelectionChange.emit(new Set(this.selectedRowIds))
        this.changeDetectorRef.markForCheck()
    }

    isRowSelected = (row: EiTableRow): boolean => {
        return this.selectedRowIds.has(this.getRowId(row))
    }

    toggleAllRowsSelection = (): void => {
        const currentPageRowIds = this.getCurrentPageRowIds()
        this.selectedRowIds = toggleAllRowsSelectionHelper(currentPageRowIds, this.selectedRowIds)
        this.rowSelectionChange.emit(new Set(this.selectedRowIds))
        this.changeDetectorRef.markForCheck()
    }

    isAllRowsSelected = (): boolean => {
        const currentPageRowIds = this.getCurrentPageRowIds()
        if (currentPageRowIds.length === 0) return false
        return currentPageRowIds.every((id) => this.selectedRowIds.has(id))
    }

    isSomeRowsSelected = (): boolean => {
        return this.getCurrentPageRowIds().some((id) => this.selectedRowIds.has(id))
    }

    startEdit = (row: EiTableRow, column: EiTableColumn): void => {
        if (!column.editable || isActionsColumn(column)) return
        const rowId = this.getRowId(row)
        this.editingCell = { rowId, colKey: column.key }
        this.editOriginalValue = getCellValue(row, column)
        this.editControl.setValue(this.editOriginalValue?.toString?.() ?? '')
        this.refreshEditInlineValidation()
    }

    isEditingCell = (row: EiTableRow, column: EiTableColumn): boolean => {
        const rowId = this.getRowId(row)
        return (
            !!this.editingCell &&
            this.editingCell.rowId === rowId &&
            this.editingCell.colKey === column.key
        )
    }

    cancelEdit = (): void => {
        this.editingCell = null
        this.editOriginalValue = null
        this.editInlineError = null
        this.editControl.setValue('')
    }

    private refreshEditInlineValidation(): void {
        this.editInlineError = null
        if (!this.editingCell) {
            return
        }
        const column = this.visibleColumns.find((c) => c.key === this.editingCell?.colKey)
        if (!column?.editValidator) {
            return
        }
        const row = this.rows.find(
            (tableRow) => String(this.getRowId(tableRow)) === String(this.editingCell?.rowId),
        )
        if (!row) {
            return
        }
        this.editInlineError = column.editValidator(this.editControl.value, row)
    }

    commitEdit = (row: EiTableRow, column: EiTableColumn): void => {
        if (!this.editingCell) return
        this.refreshEditInlineValidation()
        if (this.editInlineError) {
            return
        }
        const newValue: unknown = this.editControl.value
        const rowId = this.getRowId(row)
        this.cellEditCommit.emit({
            rowId,
            colKey: column.key,
            value: newValue,
            row: createUpdatedRow(row, column, newValue),
        })
        this.cancelEdit()
    }

    private tryCommitEditOrReset(row: EiTableRow, column: EiTableColumn): void {
        if (!this.editingCell) return
        this.refreshEditInlineValidation()

        if (this.editInlineError) {
            // In no-actions mode, invalid input should revert to original value.
            if (this.inlineEditActions === 'none') {
                this.cancelEdit()
            }
            return
        }

        this.commitEdit(row, column)
    }

    onEditKeydown = (event: KeyboardEvent, row: EiTableRow, column: EiTableColumn): void => {
        if (event.key === 'Enter') {
            event.preventDefault()
            if (this.inlineEditActions === 'none') {
                this.tryCommitEditOrReset(row, column)
            } else {
                this.commitEdit(row, column)
            }
        }
        if (event.key === 'Escape') {
            event.preventDefault()
            this.cancelEdit()
        }
    }

    onEditBlur = (row: EiTableRow, column: EiTableColumn): void => {
        if (this.inlineEditActions !== 'none') {
            return
        }
        // In no-actions mode: commit on blur if valid, else reset.
        this.tryCommitEditOrReset(row, column)
    }

    onAction = (actionId: string, row: EiTableRow) => this.actionClick.emit({ actionId, row })

    isCellClickable(column: EiTableColumn, row: EiTableRow): boolean {
        return typeof column.isCellClickable === 'function' ? column.isCellClickable(row) : false
    }

    onCellClick(event: MouseEvent, row: EiTableRow, column: EiTableColumn): void {
        event.stopPropagation()
        column.cellClick?.(row)
    }

    isActionHidden(action: EiTableAction, row: EiTableRow): boolean {
        if (typeof action.hide === 'function') {
            return action.hide(row)
        }
        return false
    }

    isActionDisabled(action: EiTableAction, row: EiTableRow): boolean {
        if (typeof action.disable === 'function') {
            return action.disable(row)
        }
        return (action.disable as boolean) ?? false
    }

    get isRowClickEnabled(): boolean {
        return this.rowClick.observed
    }

    onRowClick = (row: EiTableRow, event: MouseEvent): void => {
        if (!this.isRowClickEnabled) {
            return
        }
        const target = event.target as HTMLElement
        if (
            target.closest(
                'input, button, [role="button"], .ei-table__actions, ei-button, ei-dropdown, .ei-table__checkbox',
            )
        ) {
            return
        }
        this.rowClick.emit(row)
    }

    /** Starts edit when {@link editableCellFullTdClick} is enabled and the event hits the cell chrome. */
    onEditableTdClick(event: MouseEvent, row: EiTableRow, col: EiTableColumn): void {
        if (!this.editableCellFullTdClick) {
            return
        }
        if (
            !col.editable ||
            this.isActionsColumn(col) ||
            this.isDownloadColumn(col) ||
            col.cellTemplate
        ) {
            return
        }
        const el = event.target as HTMLElement
        if (el.closest('input, button, .ei-table__edit, .ei-table__cell-hint')) {
            return
        }
        if (this.isEditingCell(row, col)) {
            return
        }
        event.stopPropagation()
        this.startEdit(row, col)
    }

    onDownloadClick = (row: EiTableRow) => this.downloadClick.emit(row)

    getMoreAction = (): EiTableAction | undefined =>
        this.actions?.find((a) => a.opensDropdown === true || a.id === 'more')
    isSorted = (columnKey: string) => this.sort.activeKey === columnKey && !!this.sort.direction
    sortIcon = (columnKey: string) =>
        getSortIcon(columnKey, this.sort.activeKey, this.sort.direction)
    cellText = (row: EiTableRow, column: EiTableColumn) => formatCellText(row, column)

    cellTitle = (row: EiTableRow, column: EiTableColumn): string => {
        if (column.cellTitleGetter) {
            const v = column.cellTitleGetter(row)
            const s = v != null ? String(v).trim() : ''
            if (s) return s
        }
        const text = formatCellText(row, column)
        return text || (column.editable ? 'Click to ' + (column.emptyPlaceholder ?? '') : '')
    }

    cellHintTitle = (row: EiTableRow, column: EiTableColumn): string | null => {
        if (!column.cellHintTitleGetter) {
            return null
        }
        const hintTitleValue = column.cellHintTitleGetter(row)
        if (hintTitleValue == null) {
            return null
        }
        const normalizedHintTitle = String(hintTitleValue).trim()
        return normalizedHintTitle.length > 0 ? normalizedHintTitle : null
    }
    isActionsColumn = (column: EiTableColumn) => isActionsColumn(column)
    isDownloadColumn = (column: EiTableColumn) => isDownloadColumn(column)
    cellClass = (row: EiTableRow, column: EiTableColumn) =>
        column.cellClassGetter ? column.cellClassGetter(row) : (column.cellClass ?? null)
    getEmptyColspan = (): number => {
        let count = this.visibleColumns.length
        if (this.showCheckboxColumn) count++
        if (this.showRowNumberColumn) count++
        return count
    }
}
